import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Container } from "../../components/ui/Container";
import { PageContainer } from "../../components/layout/PageContainer";
import { SearchBar } from "./components/SearchBar";
import { ProductFilters } from "./components/ProductFilters";
import { ProductGrid } from "../../components/leasing-store/ProductGrid";
import { CategoryGrid } from "./components/CategoryGrid";
import { ActiveFilters } from "./components/ActiveFilters";
import { useProducts } from "./hooks/useProducts";
import { useFilters } from "./hooks/useFilters";
import { useSearch } from "./hooks/useSearch";
import { usePageLoading } from "../../hooks/usePageLoading";
import { Filter, Plus, AlertCircle, ArrowLeft } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Pagination } from "./components/Pagination";

export function LeasingStore() {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [showDetails, setShowDetails] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const toggleDetails = () => {
    setShowDetails((prev) => !prev);
  };

  const { products, isLoading } = useProducts();
  const { searchQuery, setSearchQuery, searchResults } = useSearch(products);
  
  // Utiliser le hook de chargement de page pour contrôler l'affichage du footer
  usePageLoading(isLoading);
  
  const {
    filters,
    handleFilterChange,
    removeFilter,
    clearFilters,
    filteredProducts,
  } = useFilters(searchResults);

  // Générer les catégories avec comptage
  const categories = useMemo(() => {
    const categoryMap = new Map<string, { count: number; image: string }>();
    
    searchResults.forEach(product => {
      const existing = categoryMap.get(product.category);
      if (existing) {
        existing.count++;
      } else {
        categoryMap.set(product.category, {
          count: 1,
          image: product.image
        });
      }
    });

    return Array.from(categoryMap.entries()).map(([name, data]) => ({
      name,
      count: data.count,
      image: data.image
    }));
  }, [searchResults]);

  // Filtrer par catégorie si sélectionnée
  const displayProducts = selectedCategory
    ? filteredProducts.filter(p => p.category === selectedCategory)
    : filteredProducts;

  // Pagination
  const totalPages = Math.ceil(displayProducts.length / itemsPerPage);
  const currentProducts = displayProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setCurrentPage(1);
  };

  return (
    <PageContainer>
      <div className="bg-white">
        <Container className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-baseline justify-between border-b border-gray-200 pb-6">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-gray-700 mt-4">
                Wanzo Store
              </h1>
              <h3 className="text-xl font-medium tracking-tight text-green-700 mt-2">
                Achetez, Louez, payez à votre rythme !
              </h3>
              <div className="mt-6">
                <button
                  onClick={toggleDetails}
                  className="flex items-center gap-2 text-primary hover:underline"
                >
                  <AlertCircle className="h-5 w-5" />
                  <span>En savoir plus</span>
                </button>
                {showDetails && (
                  <div className="mt-4 space-y-6 text-gray-600">
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                      <AlertCircle className="h-6 w-6 text-yellow-500 flex-shrink-0" />
                      <p className="text-justify leading-relaxed">
                        <strong>Travaillez avec des professionnels :</strong>{" "}
                        Choisissez vos équipements avec l'aide de professionnels
                        qualifiés. Des prestataires locaux peuvent également
                        concevoir vos équipements, évitant ainsi le recours à
                        l'importation.
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                      <AlertCircle className="h-6 w-6 text-yellow-500 flex-shrink-0" />
                      <p className="text-justify leading-relaxed">
                        <strong>Équipements présentés :</strong> Les équipements
                        listés ici ne sont qu'un échantillon pour illustrer les
                        types d'équipements pris en charge. Cette offre est
                        rendue possible grâce à nos institutions financières
                        partenaires, disposant des autorisations nécessaires
                        pour fournir ce service particulier.
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                      <AlertCircle className="h-6 w-6 text-yellow-500 flex-shrink-0" />
                      <p className="text-justify leading-relaxed">
                        <strong>Options d'achat :</strong> Ces équipements sont
                        également disponibles pour achat, offrant une
                        flexibilité adaptée à vos besoins professionnels.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="sticky top-16 z-10 bg-white pb-4 pt-4">
            <div className="flex items-center gap-4 mb-4">
              <Link to="/leasing-store/custom-request">
                <Button
                  variant="secondary"
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Demande personnalisée
                </Button>
              </Link>
              <Button
                variant="secondary"
                className="flex items-center gap-2 lg:hidden"
                onClick={() => setMobileFiltersOpen((prev) => !prev)}
              >
                <Filter className="h-4 w-4" />
              </Button>
            </div>

            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              onClear={() => setSearchQuery("")}
            />
          </div>

          <div className="grid grid-cols-1 gap-x-8 gap-y-10 lg:grid-cols-5 mt-8">
            {/* Filtres */}
            <aside className="lg:block">
              <div className="hidden lg:block lg:sticky lg:top-20">
                <ProductFilters
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  mobileFiltersOpen={mobileFiltersOpen}
                  setMobileFiltersOpen={setMobileFiltersOpen}
                />
              </div>
              <div className="lg:hidden">
                <ProductFilters
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  mobileFiltersOpen={mobileFiltersOpen}
                  setMobileFiltersOpen={setMobileFiltersOpen}
                />
              </div>
            </aside>

            {/* Produits */}
            <div className="lg:col-span-4">
              <ActiveFilters
                filters={filters}
                onRemoveFilter={removeFilter}
                onClearAll={clearFilters}
              />

              {isLoading ? (
                <div className="mt-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
                </div>
              ) : selectedCategory ? (
                <>
                  <div className="mb-6">
                    <Button
                      variant="secondary"
                      onClick={handleBackToCategories}
                      className="flex items-center gap-2"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Retour aux catégories
                    </Button>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">{selectedCategory}</h2>
                  <div className="mt-6">
                    <ProductGrid 
                      products={currentProducts}
                      loading={false}
                    />
                  </div>

                  {totalPages > 1 && (
                    <div className="mt-8">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-6">
                  <CategoryGrid
                    categories={categories}
                    onCategoryClick={handleCategoryClick}
                  />
                </div>
              )}
            </div>
          </div>
        </Container>
      </div>
    </PageContainer>
  );
}
